import React from 'react';

const InfutorDataDisplay = ({ data }) => {
    // Mapping objects for various field values
    const matchCodeScores = {
        '100': 'Exact First, Exact Last, Exact Street Address, Unit W/A, Zip, Phone',
        '101': 'Fuzzy First, Exact Last, Exact Street Address, Unit W/A, Zip, Phone',
        '102': 'Exact First, Fuzzy Last, Exact Street Address, Unit W/A, Zip, Phone',
        '103': 'Fuzzy First, Fuzzy Last, Exact Street Address, Unit W/A, Zip, Phone',
        '104': 'Exact First, Exact Last, Exact Street Address, Unit Missing, Zip, Phone',
        '105': 'Fuzzy First, Exact Last, Exact Street Address, Unit Missing, Zip, Phone',
        '106': 'Exact First, Fuzzy Last, Exact Street Address, Unit Missing, Zip, Phone',
        '107': 'Fuzzy First, Fuzzy Last, Exact Street Address, Unit Missing, Zip, Phone',
        '108': 'Exact First, Exact Last, Fuzzy Street Address, Zip, Phone',
        '109': 'Fuzzy First, Exact Last, Fuzzy Street Address, Zip, Phone',
        '110': 'Exact First, Fuzzy Last, Fuzzy Street Address, Zip, Phone',
        '111': 'Fuzzy First, Fuzzy Last, Fuzzy Street Address, Zip, Phone',
        '200': 'Exact Last, Exact Street Address, Unit W/A, Zip, Phone',
        '201': 'Fuzzy Last, Exact Street Address, Unit W/A, Zip, Phone',
        '202': 'Exact Last, Exact Street Address, Unit Missing, Zip, Phone',
        '203': 'Fuzzy Last, Exact Street Address, Unit Missing, Zip, Phone',
        '204': 'Exact Last, Fuzzy Street Address, Zip, Phone',
        '205': 'Fuzzy Last, Fuzzy Street Address, Zip, Phone',
        '300': 'Exact Street Address, Unit W/A, Zip, Phone',
        '301': 'Exact Street Address, Unit Missing, Zip, Phone',
        '302': 'Fuzzy Street Address, Zip, Phone',
        '400': 'Exact First, Exact Last, Phone',
        '401': 'Fuzzy First, Exact Last, Phone',
        '402': 'Exact First, Fuzzy Last, Phone',
        '403': 'Fuzzy First, Fuzzy Last, Phone',
        '450': 'Exact Last, Phone',
        '451': 'Fuzzy Last, Phone FP = First/Phone FP 460 Exact First, Phone FP 461 Fuzzy First, Phone FP 462 Fuzzy First, Phone, names reversed',
        '500': 'Zip, Phone',
        '900': 'No match found',
        '999': 'No match found',
        '': ''
    };

    const phoneMatchScores = {
        'V': 'VoIP, not subject to TCPA wireless provisions.',
        'L': 'Landline, not subject to TCPA wireless provisions.',
        'Y': 'Yes the identity verified for the wireless number using authoritative sources.',
        'YP': 'Yes the identity verified for the wireless number using authoritative sources, partial match.',
        'YP*': 'Partial verification; Infutor confirmed the ownership of the phone has not changed since the consent date, however Infutor was not able to confirm the phone owner\'s name',
        'YX': 'Yes the identity verified for the wireless number using high confidence proprietary sources (utility, financial).',
        'N': 'No, a different name was linked to the wireless phone.',
        'U': 'Unconfirmed.',
        '': 'Unidentified Phone type'
    };

    const matchCategory = {
        'IP': 'Individual with phone',
        'HP': 'HouseHold with phone',
        'AP': 'Address with phone',
        'NP': 'Name/Phone',
        'LP': 'Last Name/Phone',
        'FP': 'First Name/Phone',
        'ZP': 'Zip/Phone',
        'P': 'Phone',
        '': 'None'
    };

    const zipTypes = {
        'F': 'firm or company address',
        'G': 'General delivery address',
        'H': 'High-rise or business complex',
        'P': 'PO Box address',
        'R': 'Rural route address',
        'S': 'Street or residential address',
        '': ''
    };

    const dpvValues = {
        'Y': 'Address DPV confirmed for both primary and (if present) secondary numbers',
        'D': 'Address DPV confirmed for primary number only, secondary number information was missing',
        'S': 'Address DPV confirmed for the primary number only, and secondary number information was present but unconfirmed',
        'N': 'Both Primary and (if present) Secondary number information failed to DPV confirm',
        '': 'Address not presented to hash table'
    };

    const recordTypes = {
        'R': 'Residential',
        'B': 'Business',
        'P': 'Payphone',
        'U': 'Unknown',
        '': ''
    };

    const phoneConfidence = {
        '1': 'highest confidence',
        '2': 'high confidence',
        '3': 'high confidence',
        '4': 'possible disconnects',
        '5': 'likely disconnects',
        '6': 'disconnects',
        '7': 'unconfirmed',
        '': ''
    };

    const daCodes = {
        'y': 'Record as it appears in Directory Assistance',
        'd': 'Record was removed / delisted from Directory Assistance',
        '': 'Private record (not in Directory Assistance)'
    };

    const timeZones = {
        '2': 'Hawaii',
        '3': 'Alaska',
        '4': 'Pacific',
        '5': 'Mountain',
        '6': 'Central',
        '7': 'Eastern',
        '8': 'Atlantic'
    };

    const daylightSaving = {
        '1': 'Yes',
        '': 'No'
    };

    const mapTo = (field, value) => {
        return field[value] ? field[value] : value;
    };

    // Helper function to get nested values safely
    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined ? current[key] : '';
        }, obj);
    };

    // Helper function to format array values
    const formatArrayValue = (value) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? value.join(', ') : '';
        }
        return value || '';
    };

    // Helper function to build street address
    const buildStreetAddress = (address) => {
        if (!address) return '';
        
        const parts = [
            address.house,
            address.predir,
            address.street,
            address.strtype,
            address.postdir,
            address.apttype,
            address.aptnbr
        ].filter(part => part && part.length > 0);
        
        return parts.join(' ');
    };

    if (!data || !data.data || !data.data.Response) {
        return (
            <div className="text-center">
                <h4>No Data Available</h4>
                <p>Reason: No records found</p>
            </div>
        );
    }

    const response = data.data.Response;
    const person = response.Detail?.Person;
    const requestDate = data.requestDate;

    if (!person) {
        return (
            <div className="text-center">
                <h4>Lookup Failed</h4>
                <p>No person data found in response.</p>
            </div>
        );
    }

    // Extract data from nested structure using PascalCase field names
    const name = person.Name || {};
    const address = person.Address || {};
    const phone = person.Phone || {};

    // Create flat data structure to match the image
    const flatData = {
        Score: person.Score,
        Category_Phone_Match: person.Category_Phone_Match,
        Category: person.Category,
        MCD: person.MCD,
        PrePaid: person.PrePaid,
        CNAM: formatArrayValue(name.CNAM),
        FName: formatArrayValue(name.FName),
        MName: formatArrayValue(name.MName),
        LName: formatArrayValue(name.LName),
        BusName: formatArrayValue(name.BusName),
        House: formatArrayValue(address.House),
        PreDir: formatArrayValue(address.PreDir),
        Street: formatArrayValue(address.Street),
        StrType: formatArrayValue(address.StrType),
        PostDir: formatArrayValue(address.PostDir),
        AptType: formatArrayValue(address.AptType),
        AptNbr: formatArrayValue(address.AptNbr),
        City: formatArrayValue(address.City),
        State: formatArrayValue(address.State),
        Zip: formatArrayValue(address.Zip),
        Z4: formatArrayValue(address.Z4),
        Z4Type: formatArrayValue(address.Z4Type),
        DPV: formatArrayValue(address.DPV),
        Deliverable: formatArrayValue(address.Deliverable),
        ValDate: formatArrayValue(address.ValDate),
        Phone: formatArrayValue(phone.Phone),
        DID: formatArrayValue(phone.DID),
        RecType: formatArrayValue(phone.RecType),
        IDate: formatArrayValue(phone.IDate),
        ODate: formatArrayValue(phone.ODate),
        TelcoName: formatArrayValue(phone.TelcoName),
        PHV: formatArrayValue(phone.PHV),
        DACode: formatArrayValue(phone.DACode),
        TZ: formatArrayValue(phone.TZ),
        DSO: formatArrayValue(phone.DSO)
    };

    return (
        <div className="infutor-data-display">
            <div className="row">
                <div className="col-12">
                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead className="table-dark">
                                <tr>
                                    <th>Field Name</th>
                                    <th>Value</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Infutor Match Score</strong></td>
                                    <td>{flatData.Score}</td>
                                    <td><em>{mapTo(matchCodeScores, flatData.Score)}</em></td>
                                </tr>
                                <tr>
                                    <td><strong>Category Phone Match</strong></td>
                                    <td>{flatData.Category_Phone_Match}</td>
                                    <td><em>{mapTo(phoneMatchScores, flatData.Category_Phone_Match)}</em></td>
                                </tr>
                                <tr>
                                    <td><strong>Infutor Match Category</strong></td>
                                    <td>{flatData.Category}</td>
                                    <td><em>{mapTo(matchCategory, flatData.Category)}</em></td>
                                </tr>
                                <tr>
                                    <td><strong>MCD</strong></td>
                                    <td>{flatData.MCD}</td>
                                    <td><em>Combined Infutor match score &amp; category</em></td>
                                </tr>
                                <tr>
                                    <td><strong>Prepaid Phone</strong></td>
                                    <td>{flatData.PrePaid}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>CNAM</strong></td>
                                    <td>{flatData.CNAM}</td>
                                    <td><em>Additional name field</em></td>
                                </tr>
                                <tr>
                                    <td><strong>First Name</strong></td>
                                    <td>{flatData.FName}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Middle Name</strong></td>
                                    <td>{flatData.MName}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Last Name</strong></td>
                                    <td>{flatData.LName}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Business Name</strong></td>
                                    <td>{flatData.BusName}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Street Address</strong></td>
                                    <td>{flatData.House} {flatData.PreDir} {flatData.Street} {flatData.StrType} {flatData.PostDir} {flatData.AptType} {flatData.AptNbr}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>City</strong></td>
                                    <td>{flatData.City}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>State</strong></td>
                                    <td>{flatData.State}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Zip</strong></td>
                                    <td>{flatData.Zip}{flatData.Z4 ? ' - ' + flatData.Z4 : ''}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Zip Type</strong></td>
                                    <td>{flatData.Z4Type}</td>
                                    <td><em>{mapTo(zipTypes, flatData.Z4Type)}</em></td>
                                </tr>
                                <tr>
                                    <td><strong>Delivery Point Validation</strong></td>
                                    <td>{flatData.DPV}</td>
                                    <td><em>{mapTo(dpvValues, flatData.DPV)}</em></td>
                                </tr>
                                <tr>
                                    <td><strong>Deliverable</strong></td>
                                    <td>{flatData.Deliverable}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Last Address Validation Date</strong></td>
                                    <td>{flatData.ValDate}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Phone Number</strong></td>
                                    <td>{flatData.Phone}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Direct Inward Dial Number</strong></td>
                                    <td>{flatData.DID}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Record Type</strong></td>
                                    <td>{mapTo(recordTypes, flatData.RecType)}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>IDate</strong></td>
                                    <td>{flatData.IDate}</td>
                                    <td><em>Date record was first received</em></td>
                                </tr>
                                <tr>
                                    <td><strong>ODate</strong></td>
                                    <td>{flatData.ODate}</td>
                                    <td><em>Date record was last received as connected</em></td>
                                </tr>
                                <tr>
                                    <td><strong>Name of Telephone Company Provider</strong></td>
                                    <td>{flatData.TelcoName}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Phone Confidence Score</strong></td>
                                    <td>{flatData.PHV} - {mapTo(phoneConfidence, flatData.PHV)}</td>
                                    <td><em>Possible values 1-7 (1: highest confidence, 7: lowest confidence)</em></td>
                                </tr>
                                <tr>
                                    <td><strong>Directory Assistance Flag</strong></td>
                                    <td>{mapTo(daCodes, flatData.DACode)}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Time Zone</strong></td>
                                    <td>{mapTo(timeZones, flatData.TZ)}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td><strong>Daylight Saving Observed</strong></td>
                                    <td>{mapTo(daylightSaving, flatData.DSO)}</td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InfutorDataDisplay;
